import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import type { FeedRunResult, FeedStatus, Store } from '../api/types';
import { describeResult } from '../lib/feedOutput';

interface Props {
  store: Store;
  status: FeedStatus | undefined;
  isOwner: boolean;   // gates "Publish anyway"
  dirty?: boolean;    // an unsaved draft exists — regeneration always uses the SAVED settings
  onChanged?: () => void;
}

export default function FeedStatusCard({ store, status, isOwner, dirty = false, onChanged }: Props) {
  const qc = useQueryClient();
  const [result, setResult] = useState<FeedRunResult | null>(null);
  const regen = useMutation({
    mutationFn: (force: boolean) => api.regenerateFeed(store.id, force),
    // Clear the previous attempt's result as soon as a new one starts — otherwise a
    // stale success's `warning` keeps rendering alongside a LATER attempt's error
    // (review Finding 2).
    onMutate: () => { setResult(null); },
    onSuccess: (r) => {
      setResult(r);
      // Broad invalidate (no history suffix) intentionally matches both the
      // history=10 entries (the Feeds page) and Platform's ImageMapTable
      // (history=0) cache entries.
      qc.invalidateQueries({ queryKey: ['feed-status', store.id] });
      onChanged?.();
    },
  });
  const regenError = regen.error instanceof HttpError ? regen.error.detail
    : regen.error ? (regen.error as Error).message : null;
  const lastRun = status?.runs?.[0] ?? null;
  const showPublishAnyway = isOwner && (result?.refusal === 'ratio' || (!result && lastRun?.refusal === 'ratio'));
  const blocked = regen.isPending || dirty;

  return (
    <div className="rounded-xl border border-ff-sand/60 bg-white/40 p-3 text-[12px]" data-testid="feed-output-status">
      {status?.meta ? (
        <p>
          Last published {new Date(status.meta.last_published_at).toLocaleString()} ·{' '}
          {status.meta.items_in} in → {status.meta.items_out} out · {status.meta.items_swapped} branded ·{' '}
          {status.meta.items_dropped} left out · last check {new Date(status.meta.last_checked_at).toLocaleString()}
        </p>
      ) : (
        <p>Not published yet.</p>
      )}
      {lastRun && lastRun.status !== 'published' && lastRun.status !== 'unchanged' && (
        <p className="mt-1 text-ff-danger">
          Last run {lastRun.status}{lastRun.refusal ? ` (${lastRun.refusal})` : ''}{lastRun.error ? `: ${lastRun.error}` : ''}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => regen.mutate(false)} disabled={blocked}
          className="ff-btn-primary text-sm disabled:opacity-40" data-testid="feed-output-regenerate">
          {regen.isPending ? 'Regenerating…' : 'Regenerate now'}
        </button>
        {showPublishAnyway && (
          <button type="button" onClick={() => regen.mutate(true)} disabled={blocked}
            className="ff-btn-ghost text-sm" data-testid="feed-output-publish-anyway">
            Publish anyway
          </button>
        )}
      </div>
      {(result || regenError) && (
        <p className="mt-2 font-mono text-[11px]" data-testid="feed-output-result">
          {regenError ?? (result && describeResult(result))}
          {result?.warning ? ` · warning: ${result.warning}` : ''}
        </p>
      )}
      {status && status.runs.length > 0 && (
        <details className="mt-2" data-testid="feed-output-history">
          <summary className="cursor-pointer text-ff-mute">Recent runs</summary>
          <table className="mt-1 w-full text-[11px] font-mono">
            <tbody>
              {status.runs.map((r) => (
                <tr key={r.run_id}>
                  <td>{new Date(r.started_at).toLocaleString()}</td>
                  <td>{r.trigger}</td>
                  <td>{r.status}{r.refusal ? ` (${r.refusal})` : ''}</td>
                  <td>{r.items_out ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {dirty && (
        <p className="mt-2 text-[11px] text-ff-warning" data-testid="feeds-dirty-hint">
          Save first — regeneration always uses the saved settings.
        </p>
      )}
    </div>
  );
}
